import { h } from 'vue';
import {
  NAvatar,
  NTag,
  SelectOption,
  SelectRenderLabel,
  SelectRenderTag,
} from 'naive-ui';
import { SelectGroupOption } from 'naive-ui/es/select/src/interface';

import { authorizedSrc } from '@/core/authorizedFile';

import globalStore from './store/globalStore';
import GlobalStore, { State } from './store/globalStore';
import { ConditionType, POPULAR_FORMS, registryFilterMap } from './const';
import { getAttachmentById } from './taskInteraction';
import {
  getBotById,
  getDepartmentById,
  getFormFieldById,
  getUserById,
} from './userInteraction';

import { getUserSettings } from '@/core/api/user_api';
import {
  DetectedChangesMap,
  fieldOptionTypeGuard,
  FieldValue,
  ListElementType,
  TaskLogicHint,
  UserFieldValue,
} from '@/domain/basic';
import {
  CatalogDataType,
  CatalogListElementType,
  CatalogListElementTypeOption,
} from '@/domain/catalog';
import {
  FieldErrors,
  FieldRowFormType,
  FieldsType,
  FieldsTypeValue,
  FieldsWithValid,
  FormGroup,
  FormGroupResponse,
  SelectGroupData,
} from '@/domain/form';
import {
  HeadersTableData,
  HeadersTitleTableData,
  RegistryField,
  RegistryFieldFilter,
  RegistryFilterTags,
  RegistrySetting,
} from '@/domain/registry';
import { ChangeField, ChangeFieldValueType } from '@/domain/task';
import { User, UserSettings } from '@/domain/user';
import router from '@/router';

export const hasHistory = () => {
  return window.history.state.back !== null;
};

export const castCatalog = (
  catalog: CatalogDataType
): CatalogListElementTypeOption[] => {
  const selectCatalog: CatalogListElementTypeOption[] = [];
  if (!catalog) return [];
  const items = [...catalog.items].sort((a, b) => a.order - b.order);
  for (const row of items) {
    const new_val = row.value.join(', ');
    selectCatalog.push({
      label: new_val,
      value: new_val,
    });
  }
  return selectCatalog;
};

export const changeCatalogFiled = (catalogList: CatalogListElementType[]) => {
  return catalogList.map((n: { id: number; name: string }) => ({
    value: n.id,
    label: n.name,
  }));
};

export const timestampToDate = (timestamp: number | null) => {
  if (timestamp === null) return 'Некорректная дата';
  const res = new Date(timestamp * 1000).toLocaleDateString('ru-RU');
  return res === 'Invalid Date' ? 'Некорректная дата' : res;
};

export const timestampToTextDate = (timestamp: number) => {
  const dateWithMilliseconds = new Date(timestamp * 1000);
  const year = dateWithMilliseconds.getFullYear();
  const months = dateWithMilliseconds.getMonth();
  const dayString = dateWithMilliseconds.getDay();

  const day = dateWithMilliseconds.getDate().toString().padStart(2, '0');

  const monthsArr = [
    'Января',
    'Февраля',
    'Марта',
    'Апреля',
    'Мая',
    'Июня',
    'Июля',
    'Августа',
    'Сентября',
    'Октября',
    'Ноября',
    'Декабря',
  ];
  const days = [
    'Воскресенье',
    'Понедельник',
    'Вторник',
    'Среда',
    'Четверг',
    'Пятница',
    'Суббота',
  ];
  const res =
    days[dayString] + ', ' + day + ' ' + monthsArr[months] + ' ' + year;

  return res === 'Invalid Date' ? 'Некорректная дата' : res;
};

export const timestampToTime = (timestamp: number) => {
  const dateField = new Date(timestamp * 1000);
  return dateField
    .toLocaleTimeString('ru-RU')
    .split(':')
    .slice(0, -1)
    .join(':');
};

export const joinString = (arr: string[]) => {
  const arrNotNull = [];
  for (const el of arr) {
    if (el === undefined || el === null || el === '') {
      continue;
    }
    arrNotNull.push(el);
  }
  return arrNotNull.join(', ');
};

export const goToURL = (e: Event, url: string) => {
  e.preventDefault();
  window.open(url, '_blank');
};

export enum Hints {
  USER = 'user',
  DEPARTMENT = 'department',
  BOT = 'bot',
}

type HintsType = 'user' | 'department' | 'bot'

export const goToProfile = (authorId: number, hint: Hints | HintsType) => {
  if (hint === Hints.USER) {
    const user = getUserById(authorId);
    if (user) {
      return router.push({ name: 'Profile', params: { id: authorId } });
    }
  }
  if (hint === Hints.BOT) {
    const bot = getBotById(authorId);
    if (bot) {
      return router.push({ name: 'Bot', params: { id: authorId } });
    }
  }

  if (hint === Hints.DEPARTMENT) {
    const department = getDepartmentById(authorId);
    if (department) {
      return router.push({
        name: 'Department',
        params: { id: authorId },
      });
    }
  }

  const bot = getBotById(authorId);
  if (bot) {
    return router.push({ name: 'Bot', params: { id: authorId } });
  }
};

export function urlForDownload(url: string | null, avatar_size = 0) {
  if (url === '' || url === undefined) return '';
  if (url === null) {
    return ' ';
  }
  let path = url;
  if (path[0] !== undefined && path[0] !== '/') {
    path = `/${path}`;
  }
  if (avatar_size === 0) {
    return `${import.meta.env.VITE_RADAR_URL}${path}`;
  }

  return `${
    import.meta.env.VITE_RADAR_URL
  }${path}?avatar_size=${avatar_size}`;
}

export function isOverflown(element: HTMLElement) {
  return (
    element.scrollHeight > element.clientHeight ||
    element.scrollWidth > element.clientWidth
  );
}

export const isValidEmail = (email: FieldValue) => {
  if (email === '' || email === undefined || email === null) return true;
  return /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
    String(email).toLowerCase()
  );
};

export const isValidTime = (textValue: FieldValue) => {
  if (textValue === '' || textValue === null || textValue === undefined)
    return true;
  return /^([0-1][0-9]|2[0-3]):([0-5][0-9])$/g.test(String(textValue));
};

export const isValidPhoneNumber = (
  phoneNumber: FieldValue | undefined
): boolean => {
  if (phoneNumber === '' || phoneNumber === undefined) return true;

  // Убираем все символы, кроме цифр и "+"
  const formatted = String(phoneNumber).replace(/[^0-9+]/g, '');

  // Регулярное выражение для проверки формата номера
  const pattern = /^\+?(\d{1,3})?\d{10}$/;

  // Проверка номера по регулярному выражению
  if (!pattern.test(formatted)) {
    return false;
  }

  // Проверка, чтобы общая длина номера (только цифры) была правильной
  const digitsOnly = formatted.replace(/\D/g, '');
  return (
    digitsOnly.length === 10 ||
    (digitsOnly.length >= 11 && digitsOnly.length <= 13)
  );
};

export const isValidField = (
  type: string,
  value: FieldValue | null | undefined,
  hint: 'task' | 'form' | 'chat',
  textValue?: string
): boolean => {
  if (type === 'time') return isValidTime(textValue);

  if (value === null) return true;
  if (textValue === '') return true;
  if (value === undefined && (textValue === '' || textValue === undefined))
    return true;

  if (type === 'email') return isValidEmail(value);
  if (type === 'phone') return isValidPhoneNumber(value);

  return true;
};

export const arrayValueEquals = (
  array1: Array<unknown>,
  array2: Array<unknown>
) => {
  return (
    array1.every((item) => array2.includes(item)) &&
    array2.every((item) => array1.includes(item))
  );
};

export const recursiveAddTableValid = (
  fieldsValues: FieldRowFormType<FieldsTypeValue>[],
  currentStage: number,
  hint: 'task' | 'chat' | 'form'
): FieldRowFormType<FieldsWithValid>[] => {
  return fieldsValues.map((field) => {
    return {
      cells: recursiveAddValid(field.cells, currentStage, hint),
      row_id: field.row_id,
    };
  });
};

export const recursiveAddValid = (
  fieldWithChanges: FieldsTypeValue[],
  currentStage: number,
  hint: 'task' | 'chat' | 'form'
): FieldsWithValid[] => {
  const returnFields = Array.from(fieldWithChanges) as FieldsWithValid[];
  return returnFields.map((field: FieldsWithValid) => {
    if (field.fields !== undefined) {
      field.fields = recursiveAddValid(field.fields, currentStage, hint);
    }

    if (
      field.values !== undefined &&
      field.values !== null &&
      (field.values.length > 1 ||
        (field.values.length === 1 && field.values[0].row_id !== null))
    ) {
      field.values = recursiveAddTableValid(field.values, currentStage, hint);
    }

    if (field.type === 'time' && hint === 'chat' && field.value !== null) {
      return {
        ...field,
        isRequiredError: false,
        isValid: true,
      };
    }

    if (
      ((field.type === 'time' &&
        (field.textValue === undefined || field.textValue === '')) ||
        (field.type !== 'time' && field.value === null) ||
        (field.type !== 'time' && field.value === '') ||
        (field.type !== 'time' && field.value === undefined) ||
        (Array.isArray(field.value) && !field.value.length) ||
        (field.type === 'checkmark' && field.value === false) ||
        (field.type === 'routing' &&
          (field.value as UserFieldValue).users.length === 0 &&
          (field.value as UserFieldValue).departments.length === 0) ||
        (field.type === 'date' && field.value === null)) &&
      field.required_on.includes(currentStage)
    ) {
      return {
        ...field,
        isRequiredError: true,
        isValid: isValidField(field.type, field.value, hint, field.textValue),
      };
    }
    return {
      ...field,
      isRequiredError: false,
      isValid: isValidField(field.type, field.value, hint, field.textValue),
    };
  });
};

const recursiveEmittingRowFields = (
  values: FieldRowFormType<FieldsWithValid>[],
  hint: TaskLogicHint
): FieldRowFormType<FieldErrors>[] => {
  return values.map((value) => {
    return {
      cells: recursiveEmittingFields(value.cells, hint, value.row_id).map(
        (errors) => {
          if (value.row_id !== null && value.row_id !== undefined) {
            return {
              ...errors,
              rowId: value.row_id,
            };
          }
          return errors;
        }
      ),
      row_id: value.row_id,
    };
  });
};

export const recursiveEmittingFields = (
  fieldWithChanges: FieldsWithValid[],
  hint: TaskLogicHint = 'form',
  rowId?: number | null
): FieldErrors[] => {
  const returnFields = Array.from(fieldWithChanges);
  return returnFields.map((field) => {
    if (field.fields) {
      return {
        id: field.id,
        fields: recursiveEmittingFields(field.fields, hint),
        isRequiredError:
          hint === 'form' && field.isRequiredError !== undefined
            ? field.isRequiredError
            : false,
        isValid: field.isValid === undefined || field.isValid,
        values: [],
      };
    }

    if (field.type === 'table') {
      return {
        id: field.id,
        fields: field.fields ?? [],
        isRequiredError: hint === 'form' ? field.isRequiredError : false,
        isValid: field.isValid,
        values: recursiveEmittingRowFields(field.values, hint),
      };
    }

    if (rowId !== undefined) {
      return {
        id: field.id,
        fields: [],
        isRequiredError:
          hint === 'form' &&
          field.isRequiredError !== undefined &&
          rowId !== null
            ? field.isRequiredError
            : false,
        isValid: field.isValid,
        values: [],
      };
    }

    return {
      id: field.id,
      fields: [],
      isRequiredError:
        hint === 'form' && field.isRequiredError !== undefined
          ? field.isRequiredError
          : false,
      isValid: field.isValid,
      values: [],
    };
  });
};

export const recursivePushChangeField = (
  fields: FieldsTypeValue[],
  changeField: ChangeField
) => {
  const returnFields = Array.from(fields);
  returnFields.forEach((field: FieldsTypeValue) => {
    if (field.fields) {
      field.fields = recursivePushChangeField(field.fields, changeField);
    } else {
      if (field.id === changeField.field_id) {
        if (changeField.textValue) {
          field.textValue = changeField.textValue;
        } else {
          field.value = changeField.value;
        }
      }
    }

    if (field.values) {
      field.values = pushChangeRowField(field.values, changeField);
    }
  });
  return returnFields;
};

export const pushChangeRowField = (
  values: FieldRowFormType<FieldsTypeValue>[],
  changeField: ChangeField
) => {
  const returnFields = Array.from(values);
  for (let i = 0; i < returnFields.length; i++) {
    if (changeField.row_id === returnFields[i].row_id) {
      returnFields[i].cells.forEach((cell) => {
        if (cell.id === changeField.field_id) {
          cell.value = changeField.value;
        }
      });
    }
  }
  return returnFields;
};

export const isFieldsTableHaveError = (
  values: FieldRowFormType<FieldErrors>[],
  hint: TaskLogicHint
): boolean[] => {
  const res: boolean[] = [];
  for (let i = 0; i < values.length; i++) {
    values[i].cells.forEach((cell) => {
      // необходима проверка на cell.isValid === false
      if (
        (hint === 'form' && (cell.isRequiredError || cell.isValid === false)) ||
        (hint === 'task' && cell.isValid === false)
      ) {
        res.push(true);
      }
    });
  }
  return res;
};
export const isFieldsHaveError = (
  fields: FieldErrors[],
  hint: TaskLogicHint
): boolean[] => {
  const res: boolean[] = [];
  for (let i = 0; i < fields.length; i++) {
    if (fields[i].fields.length) {
      res.push(...isFieldsHaveError(fields[i].fields, hint));
    } else {
      if (
        (hint === 'form' &&
          (fields[i].isRequiredError || !fields[i].isValid)) ||
        (hint === 'task' && !fields[i].isValid)
      ) {
        res.push(true);
      }
    }

    if (fields[i].values.length) {
      res.push(...isFieldsTableHaveError(fields[i].values, hint));
    }
  }

  return res;
};

export const getTimeString = (timestamp: number): string => {
  if (timestamp === null || timestamp === undefined) return '';

  // Добавляем смещение UTC+3 (10800 секунд)
  const totalSeconds = timestamp + 10800;

  const hours = Math.floor((totalSeconds / 3600) % 24);
  const minutes = Math.floor((totalSeconds % 3600) / 60);

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(
    2,
    '0'
  )}`;
};

export const timeStringToTimeStamp = (timeStr: string): null | number => {
  if (timeStr === '') return null;
  const [hours, minutes] = timeStr.split(':').map(Number);
  return (hours - 3) * 3600 + minutes * 60; //Убираем смещение UTC+3
};

export const findFieldConvertValue = (
  fieldValue: ChangeFieldValueType,
  isOld = false
) => {
  let res = null;
  const field = getFormFieldById(fieldValue.field_id);
  const value = isOld === false ? fieldValue.value : fieldValue.old_value;
  if (!field || !value) {
    return null;
  }
  if (field.type === 'due_date_time') {
    res = `${timestampToDate(value as number)} ${timestampToTime(
      value as number
    )}`;
  } else if (['due_date', 'date'].includes(field.type)) {
    res = timestampToDate(value as number);
  } else if (field.type === 'time') {
    res = timestampToTime(Math.abs(value as number));
  } else if (field.type === 'checkmark') {
    res = value === true ? 'Да' : 'Нет';
  } else if (field.type === 'file') {
    const new_value = [];
    for (const val of value as number[]) {
      const attach = getAttachmentById(val);
      if (!attach) {
        new_value.push('Неизвестное вложение');
        continue;
      }
      new_value.push(
        attach.file_path.slice(attach.file_path.lastIndexOf('/') + 1)
      );
    }
    res = new_value;
  } else if (field.type === 'routing') {
    if (Array.isArray(value)) {
      const arrVal = value as unknown as UserFieldValue[];
      for (const userVal of arrVal) {
        const userValue = userVal as UserFieldValue;
        const values = [];
        for (const user_id of userValue.users) {
          const user = getUserById(user_id);
          if (!user) {
            const bot = getBotById(user_id);
            values.push(bot?.info.bot_name);
            continue;
          }
          values.push(`${user.info.name || ''} ${user.info.surname || ''}`);
        }
        for (const dep_id of userValue.departments) {
          const dep = getDepartmentById(dep_id);
          values.push(dep?.name);
        }
        res = values.join(', ');
      }
    } else {
      const userValue = value as UserFieldValue;
      const values = [];
      for (const user_id of userValue.users) {
        const user = getUserById(user_id);
        if (!user) {
          const bot = getBotById(user_id);
          values.push(bot?.info.bot_name);
          continue;
        }
        values.push(`${user.info.name || ''} ${user.info.surname || ''}`);
      }
      for (const dep_id of userValue.departments) {
        const dep = getDepartmentById(dep_id);
        values.push(dep?.name);
      }
      res = values.join(', ');
    }
  } else {
    res = value ? value.toString() : null;
  }
  return res;
};

export const getStoreCatalog = (cell: FieldsTypeValue) => {
  const catalogs = globalStore.getters.getCatalogs as CatalogDataType[];

  const catalog = catalogs.find(
    (catalog) => catalog.id === cell.info?.catalog_id
  );
  if (!catalog) return [];
  const allValues = castCatalog(catalog);
  const pickedValues: CatalogListElementTypeOption[] = [];

  const cellValues = Array.isArray(cell.value)
    ? (cell.value as string[])
    : (cell.value as string);
  if (cellValues === null) return [];

  allValues.forEach((catalogValue) => {
    if (
      (Array.isArray(cellValues) && cellValues.includes(catalogValue.value)) ||
      (typeof cellValues === 'string' && catalogValue.value === cellValues)
    ) {
      pickedValues.push(catalogValue);
    }
  });
  return pickedValues;
};

export const sortedTextLengthInCol = (
  values: FieldRowFormType<FieldsTypeValue>[]
) => {
  const sortedByOrder: Map<
    string | number,
    number | string | Record<string, number>
  > = new Map();
  for (let i = 0; i < values.length; i++) {
    values[i].cells.forEach((cell) => {
      const order = String(cell.order);
      switch (cell.type) {
        case 'text':
        case 'number':
        case 'email':
        case 'phone':
        case 'money':
          if (sortedByOrder.has(order)) {
            const elem = sortedByOrder.get(order) as number;
            sortedByOrder.set(
              order,
              Math.max(elem, calcTextRenderLength(String(cell.value)))
            );
          } else {
            sortedByOrder.set(order, calcTextRenderLength(String(cell.value)));
          }
          break;
        case 'multi_catalog':
        case 'catalog':
          if (sortedByOrder.has(order)) {
            const elem = sortedByOrder.get(order) as Record<string, number>;
            const pickedValues = getStoreCatalog(cell);
            const textLength = Math.max(
              ...pickedValues.map((value) =>
                calcTextRenderLength(String(value.label))
              )
            );
            const key =
              cell.type === 'catalog' ? 'catalogLength' : 'multiCatalogLength';
            const value =
              cell.type === 'catalog'
                ? Math.max(elem.catalogLength, textLength)
                : Math.max(elem.multiCatalogLength, textLength);
            sortedByOrder.set(order, { [key]: value });
          } else {
            const pickedValues = getStoreCatalog(cell);
            const textOptionLength = Math.max(
              ...pickedValues.map((value) =>
                calcTextRenderLength(String(value.label))
              )
            );
            const key =
              cell.type === 'catalog' ? 'catalogLength' : 'multiCatalogLength';

            sortedByOrder.set(order, { [key]: textOptionLength });
          }
          break;
        case 'checkmark':
        case 'time':
        case 'date':
          sortedByOrder.set(order, cell.type);
      }
    });
  }
  return sortedByOrder;
};

export const calcTextRenderLength = (phrase: string, fontsize = '14px') => {
  const inlineElement = document.createElement('span');
  inlineElement.innerHTML = phrase;
  inlineElement.style.fontSize = fontsize;
  inlineElement.style.position = 'absolute';
  inlineElement.style.visibility = 'hidden';
  document.body.append(inlineElement);
  const width = inlineElement.clientWidth;
  inlineElement.remove();
  return width;
};
export const findTableFields = (fields: FieldsTypeValue[]) => {
  const res: FieldsTypeValue[] = [];

  fields.forEach((field) => {
    if (field.fields) {
      res.push(...findTableFields(field.fields));
    } else if (field.type === 'table') {
      res.push(field);
    }
  });
  return res;
};

export const getAllFields = (
  fields: FieldsType[] | FieldsTypeValue[]
): Array<FieldsType | FieldsTypeValue> => {
  const res: Array<FieldsType | FieldsTypeValue> = [];

  fields.forEach((field) => {
    if (field.fields) {
      res.push(...getAllFields(field.fields));
    } else {
      res.push(field);
    }
  });
  return res;
};

export const getAllFieldsErrors = (fields: FieldErrors[]): FieldErrors[] => {
  const res: FieldErrors[] = [];

  fields.forEach((field) => {
    res.push(field);
    if (field.fields) {
      res.push(...getAllFieldsErrors(field.fields));
    }
    if (field.values) {
      field.values.forEach((value) => {
        res.push(...getAllFieldsErrors(value.cells));
      });
    }
  });
  return res;
};

export const getAllFormFields = (
  fields: FieldsType[] | FieldsTypeValue[]
): Array<FieldsType | FieldsTypeValue> => {
  const res: Array<FieldsType | FieldsTypeValue> = [];
  fields.forEach((field) => {
    if (field.fields) {
      res.push(field);
      res.push(...getAllFormFields(field.fields));
    } else {
      res.push(field);
    }
  });
  return res;
};

export const getRegistryFields = (
  fields: FieldsTypeValue[]
): RegistryField[] => {
  const res: RegistryField[] = [];
  fields.forEach((field) => {
    if (field.fields) {
      res.push(...getRegistryFields(field.fields));
    } else {
      if (
        field.type !== 'table' &&
        field.type !== 'file' &&
        field.type !== 'note' &&
        field.type !== 'title'
      ) {
        res.push({
          text: field.name,
          id: field.id,
          dragStart: false,
          order: field.order,
          dragOver: false,
          hidden: false,
        });
      }
    }
  });
  return res;
};

export const getNestedConstructorFeild = (
  fields: FieldsType[],
  id: number
): FieldsType | undefined => {
  const field = fields.find((field) => field.id === id);
  if (field) return field as FieldsType;
  for (let i = 0; i < fields.length; i++) {
    if (fields[i].id === id) {
      return fields[i];
    }
    if (fields[i].fields) {
      const nestedfields = fields[i].fields as FieldsType[];
      return getNestedConstructorFeild(nestedfields, id);
    }
  }
};

export const compareOptionFields = (
  conditionType: number,
  value: FieldValue,
  refValue: FieldValue
) => {
  const getUserFieldLists = (fieldValue: unknown) => {
    if (!fieldValue || typeof fieldValue !== 'object') {
      return { users: [] as number[], departments: [] as number[] };
    }

    const maybeUsers = (fieldValue as UserFieldValue).users;
    const maybeDepartments = (fieldValue as UserFieldValue).departments;

    return {
      users: Array.isArray(maybeUsers) ? maybeUsers : [],
      departments: Array.isArray(maybeDepartments) ? maybeDepartments : [],
    };
  };

  switch (conditionType) {
    case ConditionType.Equal:
      if (value === refValue) return true;
      if (typeof refValue === 'boolean' && Boolean(value) === refValue) {
        return true;
      }
      if (
        typeof value === 'boolean' &&
        ((value === true && (refValue === 1 || refValue === true)) ||
          (value === false && (refValue === 0 || refValue === false)))
      ) {
        return true;
      } else if (typeof value === 'boolean') {
        return false;
      }
      if (Array.isArray(value) && Array.isArray(refValue)) {
        for (const refVal of refValue as string[]) {
          if ((value as string[]).includes(refVal)) {
            return true;
          }
        }
        return false;
      }
      if (
        typeof value === 'object' &&
        (value as UserFieldValue) &&
        'users' in (value as UserFieldValue)
      ) {
        const valueLists = getUserFieldLists(value);
        const refLists = getUserFieldLists(refValue);
        return (
          refLists.users.some((u) => valueLists.users.includes(u)) ||
          refLists.departments.some((u) => valueLists.departments.includes(u))
        );
      }
      return false;
    case ConditionType.NotEqual:
      if (
        typeof value === 'object' &&
        typeof refValue === 'object' &&
        (value as UserFieldValue) &&
        (refValue as UserFieldValue) &&
        'users' in (value as UserFieldValue) &&
        'users' in (refValue as UserFieldValue)
      ) {
        const valueLists = getUserFieldLists(value);
        const refLists = getUserFieldLists(refValue);
        return !(
          refLists.users.some((u) => valueLists.users.includes(u)) ||
          refLists.departments.some((u) => valueLists.departments.includes(u))
        );
      }
      if (
        value !== refValue &&
        (!Array.isArray(value) || !Array.isArray(refValue))
      )
        return true;
      if (typeof refValue === 'boolean' && Boolean(value) !== refValue) {
        return true;
      }
      if (Array.isArray(value) && Array.isArray(refValue)) {
        for (const refVal of refValue as string[]) {
          if ((value as string[]).includes(refVal)) {
            return false;
          }
        }
        return true;
      }

      return false;
    case ConditionType.NotEmpty:
      if (
        value === null ||
        value === '' ||
        (Array.isArray(value) && !value.length)
      ) {
        return false;
      }
      if (typeof value === 'object' && (value as UserFieldValue)) {
        const valueLists = getUserFieldLists(value);
        let users = false,
          departments = false;

        if ('users' in value) {
          if (valueLists.users.length > 0) {
            users = true;
          }
        }

        if ('departments' in value) {
          if (valueLists.departments.length > 0) {
            departments = true;
          }
        }

        return users || departments;
      }
      return true;
    case ConditionType.Empty:
      if (
        value === null ||
        value === '' ||
        (Array.isArray(value) && !value.length)
      ) {
        return true;
      }
      if (typeof value === 'object' && (value as UserFieldValue)) {
        const valueLists = getUserFieldLists(value);
        return !(
          valueLists.users.length > 0 || valueLists.departments.length > 0
        );
      }
      return false;
    case ConditionType.StartWithValue:
      if (refValue === null || value === null) return false;
      if (Array.isArray(value)) {
        for (let i = 0; i < value.length; i++) {
          if (
            String(value[i])
              .toLowerCase()
              .startsWith(String(refValue).toLowerCase())
          )
            return true;
        }
        return false;
      }
      if (
        String(value).toLowerCase().startsWith(String(refValue).toLowerCase())
      )
        return true;
      return false;
    case ConditionType.EndWithValue:
      if (refValue === null || value === null) return false;

      if (Array.isArray(value)) {
        for (let i = 0; i < value.length; i++) {
          if (
            String(value[i])
              .toLowerCase()
              .endsWith(String(refValue).toLowerCase())
          )
            return true;
        }
      }
      if (String(value).toLowerCase().endsWith(String(refValue).toLowerCase()))
        return true;
      return false;
    case ConditionType.IncludesValue:
      if (refValue === null || value === null) return false;
      if (Array.isArray(value)) {
        for (let i = 0; i < value.length; i++) {
          if (
            String(value[i])
              .toLowerCase()
              .includes(String(refValue).toLowerCase())
          )
            return true;
        }
        return false;
      }
      if (String(value).toLowerCase().includes(String(refValue).toLowerCase()))
        return true;
      return false;
    default:
      return false;
  }
};

export const findElWithRecursion = (el: FieldsType, element: FieldsType) => {
  if ('fields' in el) {
    el.fields?.map((item) => {
      if (item.id === element.id) {
        el.fields = el.fields?.filter((col) => col.id !== item.id);
      } else {
        findElWithRecursion(item, element);
      }
    });
  } else if ('columns' in el) {
    el.columns?.map((item) => {
      if (item.id === element.id) {
        el.columns = el.columns?.filter((col) => col.id !== item.id);
      } else {
        findElWithRecursion(item, element);
      }
    });
  } else {
    return;
  }
};

export const getContructorFieldOptions = (
  fields: FieldsType[]
): FieldsType[] => {
  const necessaryFields = fields.filter((field) =>
    fieldOptionTypeGuard(field.type)
  );
  for (let i = 0; i < necessaryFields.length; i++) {
    if (necessaryFields[i].fields) {
      const nestedFeilds = necessaryFields[i].fields as FieldsType[];
      necessaryFields.push(...getContructorFieldOptions(nestedFeilds));
    }
  }
  return necessaryFields;
};

export const renderMultipleSelectTag: SelectRenderTag = ({
  option,
  handleClose,
}: {
  option: SelectOption;
  handleClose: () => void;
}) => {
  return h(
    NTag,
    {
      round: true,
      closable: true,
      onClose: (e: any) => {
        e.stopPropagation();
        handleClose();
      },
    },
    {
      default: () =>
        h(
          'div',
          {
            style: {
              display: 'flex',
              alignItems: 'center',
            },
          },
          [
            h(NAvatar, {
              src: authorizedSrc(`${option.avatarUrl}`),
              round: true,
              size: 18,
              style: {
                marginRight: '4px',
              },
            }),
            option.label as string,
          ]
        ),
    }
  );
};

export const renderMultipleTag: SelectRenderTag = ({
  option,
  handleClose,
}: {
  option: SelectOption;
  handleClose: () => void;
}) => {
  return h(
    NTag,
    {
      round: true,
      closable: true,
      onClose: (e: any) => {
        e.stopPropagation();
        handleClose();
      },
    },
    {
      default: () =>
        h(
          'div',
          {
            style: {
              display: 'flex',
              alignItems: 'center',
            },
          },
          [option.label as string]
        ),
    }
  );
};

export const renderSingleSelectTag: SelectRenderTag = ({ option }) => {
  return h(
    'div',
    {
      style: {
        display: 'flex',
        alignItems: 'center',
      },
    },
    [
      option.avatarUrl
        ? h(NAvatar, {
            src: authorizedSrc(`${option.avatarUrl}`),
            round: true,
            size: 24,
            style: {
              marginRight: '12px',
            },
          })
        : '',
      typeof option.label === 'string' && option.label === option.value
        ? 'Выберите участника'
        : (option.label as string),
    ]
  );
};

export const renderLabel: SelectRenderLabel = (
  option: SelectOption | SelectGroupOption,
  selected: boolean
) => {
  return h(
    'span',
    {
      style: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      },
    },
    [
      h(NAvatar, {
        src: authorizedSrc(`${option.avatarUrl}`),
        round: true,
        size: 22,
      }),
      h('div', [h('div', null, [option.label as string])]),
    ]
  );
};

export const transformDate = (timestamp: number): string => {
  const time = Math.abs(timestamp);
  const countDays = Math.floor(time / (1000 * 60 * 60 * 24));
  const countHours = Math.floor(
    (time % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
  );
  const countMinutes = Math.floor((time % (1000 * 60 * 60)) / (1000 * 60));
  const countYears = Math.floor(time / (1000 * 60 * 60 * 24 * 365));
  const years = countYears !== 0 ? `${countYears}г` : '';
  const days = countDays !== 0 ? `${countDays}д` : '';
  const hours = countHours !== 0 ? `${countHours}ч` : '';
  const minutes = countMinutes !== 0 ? `${countMinutes}м` : '';
  let res = `${years}`;
  if (years === '') {
    res += `${days}`;
  }
  if (days === '') {
    res += `${hours} ${minutes}`;
  }

  if (timestamp < 0) return res ? `-${res}` : '';
  return res;
};

export const recursiveGetForms = (
  formGroups: FormGroupResponse
): ListElementType[] => {
  const forms: ListElementType[] = [];

  if (formGroups.forms.length > 0) {
    forms.push(...formGroups.forms);
  }

  if (formGroups.groups.length > 0) {
    formGroups.groups.forEach((group) => {
      forms.push(...recursiveGetForms(group));
    });
  }

  return forms;
};

export const recuresiveGetGroups = (formGroups: FormGroup): SelectGroupData => {
  const returnGroupData: SelectGroupData = {
    label: formGroups.name,
    key: String(formGroups.id),
    children: [],
  };

  if (formGroups.groups.length > 0) {
    formGroups.groups.forEach((group) => {
      returnGroupData.children.push(recuresiveGetGroups(group));
    });
  }

  return returnGroupData;
};

export const detectArrayChanges = (
  prevItems: string[],
  newItems: string[]
): DetectedChangesMap => {
  const detectedChanges: DetectedChangesMap = {
    addedItems: [],
    removedItems: [],
  };

  for (let i = 0; i < newItems.length; i++) {
    const nextItem = newItems[i];
    if (!prevItems.includes(nextItem)) {
      detectedChanges.addedItems.push(nextItem);
    }
  }

  for (let i = 0; i < prevItems.length; i++) {
    const prevItem = prevItems[i];
    if (!newItems.includes(prevItem)) {
      detectedChanges.removedItems.push(prevItem);
    }
  }

  return detectedChanges;
};

export const sortTaskListTabs = (
  tabs: ListElementType<string>[]
): ListElementType<string>[] => {
  const returnTabs: ListElementType<string>[] = [];
  POPULAR_FORMS.forEach((form) => {
    const tab = tabs.find((tab) => tab.name === form);
    if (tab) {
      returnTabs.push(tab);
    }
  });

  tabs.forEach((tab) => {
    if (!returnTabs.find((returnTab) => returnTab.name === tab.name)) {
      returnTabs.push(tab);
    }
  });

  return returnTabs;
};

export const debounce = <T>(callBack: (...arg: T[]) => void, ms: number) => {
  let lastCall = 0;
  let lastCallTimer: null | ReturnType<typeof setTimeout> = null;
  return (...arg: T[]) => {
    const prevCall = lastCall;
    lastCall = Date.now();

    if (prevCall && lastCall - prevCall < ms) {
      lastCallTimer && clearTimeout(lastCallTimer);
    }
    lastCallTimer = setTimeout(() => {
      callBack(...arg);
    }, ms);
  };
};

type ThrottleFunction<T extends (...args: any[]) => any> = {
  (this: ThisParameterType<T>, ...args: Parameters<T>):
    | ReturnType<T>
    | undefined;
};

export function throttle<T extends (...args: any[]) => any>(
  func: T,
  delay: number
): ThrottleFunction<T> {
  let lastCallTime = 0;
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return function (this: ThisParameterType<T>, ...args: Parameters<T>) {
    const currentTime = new Date().getTime();

    const execute = () => {
      func.apply(this, args);
      lastCallTime = currentTime;
    };

    if (!lastCallTime || currentTime - lastCallTime >= delay) {
      execute();
    } else if (!timeout) {
      timeout = setTimeout(() => {
        execute();
        timeout = null;
      }, delay - (currentTime - lastCallTime));
    }
  } as ThrottleFunction<T>;
}

export const createColumns = (
  fields: FieldsTypeValue[],
  settings: RegistrySetting[]
): HeadersTableData[] => {
  const returnFields: HeadersTableData[] = [];

  const newFields: HeadersTableData[] = [];
  for (let i = 0; i < fields.length; i++) {
    const field = fields[i];
    if (
      field.type === 'title' ||
      field.type === 'file' ||
      field.type === 'table' ||
      field.type === 'note'
    )
      continue;

    const settingField = settings.find(
      (setting) => setting.field_id === field.id
    );
    if (settingField) {
      returnFields.push({
        fieldId: field.id,
        fieldType: field.type,
        key: `${field.id}`,
        minWidth: 250,
        order: settingField ? settingField.order : returnFields.length,
        title: field.name,
        type: field.type,
        parentId: field.parent_id,
        hidden: settingField.hidden,
        isFilterOpen: false,
        catalogId: field.info?.catalog_id || null,
      });
    } else {
      newFields.push({
        fieldId: field.id,
        fieldType: field.type,
        key: `${field.id}`,
        minWidth: 250,
        order: newFields.length,
        title: field.name,
        type: field.type,
        parentId: field.parent_id,
        hidden: false,
        isFilterOpen: false,
        catalogId: field.info?.catalog_id || null,
      });
    }
  }

  for (let i = 0; i < settings.length; i++) {
    const setting = settings[i];
    if (setting.field_id === -1 || setting.field_id === -2) {
      returnFields.push({
        fieldId: setting.field_id,
        fieldType: 'system_date',
        key: `${setting.field_id}`,
        minWidth: 250,
        order: setting.order,
        title:
          setting.field_id === -1
            ? 'Дата завершения'
            : 'Дата последней активности',
        type: 'date',
        parentId: null,
        hidden: setting.hidden,
        isFilterOpen: false,
      });
    }
  }

  return returnFields
    .sort((a, b) => a.order - b.order)
    .concat(newFields)
    .map((field, index) => ({ ...field, order: index }));
};
export const createTitleColumns = (
  fields: FieldsTypeValue[],
  columns: HeadersTableData[]
): HeadersTitleTableData[] => {
  const returnFields: HeadersTitleTableData[] = [];
  const titleFields = fields.filter((field) => field.type === 'title');

  for (let i = 0; i < columns.length; i++) {
    const column = columns[i];
    if (!column.parentId) {
      returnFields.push({
        title: '',
        key: `${column.fieldId}`,
        type: column.type,
        order: column.order,
        fieldType: column.fieldType,
        minWidth: 250,
        fieldId: column.fieldId,
        isTitle: true,
        colSpan: 0,
        parentId: column.parentId,
        hidden: column.hidden,
        isFilterOpen: false,
      });
    } else {
      if (columns[i - 1] && column.parentId === columns[i - 1].parentId) {
        const parentColumnIndex = returnFields.findIndex(
          (col) => col.fieldId === columns[i - 1].parentId
        );
        if (parentColumnIndex !== -1 && !column.hidden) {
          if (returnFields[parentColumnIndex].colSpan === 0) {
            returnFields[parentColumnIndex].colSpan = 2;
          } else {
            returnFields[parentColumnIndex].colSpan++;
          }
        }
      } else {
        const titleField = titleFields.find(
          (col) => col.id === column.parentId
        );
        if (titleField && titleField.type !== 'table') {
          returnFields.push({
            title: titleField.name || '',
            key: `${titleField.id}`,
            type: titleField.type,
            order: column.order,
            fieldType: titleField.type,
            minWidth: 250,
            fieldId: titleField.id,
            isTitle: true,
            colSpan: 0,
            parentId: titleField.id,
            hidden: column.hidden,
            isFilterOpen: false,
          });
        }
      }
    }
  }
  returnFields.sort((a, b) => a.order - b.order);
  return returnFields;
};

export const renderTagValue = (
  param: FieldValue,
  key: keyof typeof registryFilterMap
): string => {
  if (typeof param === 'boolean') return '';
  if (typeof param === 'string') {
    return String(param);
  }
  if (
    typeof param === 'number' &&
    (key === 'date_end' || key === 'date_start')
  ) {
    return new Date(param * 1000).toLocaleString('ru-RU', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
  if (typeof param === 'number') {
    return String(param);
  }

  if (Array.isArray(param) && typeof param[0] === 'string') {
    return param.join('; ');
  }
  if (
    Array.isArray(param) &&
    typeof param[0] === 'number' &&
    key === 'user_ids'
  ) {
    return (param as number[])
      .map((partis) => {
        const user = getUserById(partis);

        if (user) {
          return `${user.info.name || ''} ${user.info.surname || ''}`;
        }
        return '';
      })
      .join('; ');
  }
  if (
    Array.isArray(param) &&
    typeof param[0] === 'number' &&
    key === 'department_ids'
  ) {
    return (param as number[])
      .map((partis) => getDepartmentById(partis)?.name || '')
      .join('; ');
  }
  if (typeof param === 'object' && !Array.isArray(param) && param) {
    return [
      ...param.departments.map((dep) => getDepartmentById(dep)?.name || ''),
      ...param.users.map((user) => {
        const userInfo = getUserById(user);
        return userInfo
          ? `${userInfo.info.name || ''} ${userInfo.info.surname || ''}`
          : '';
      }),
    ].join('; ');
  }
  return '';
};

export const updateRegistryFilterTags = (
  filters: RegistryFieldFilter[],
  fields: RegistryField[]
) => {
  const filterTags: RegistryFilterTags[] = [];

  filters.forEach((filter) => {
    const field = fields.find((field) => field.id === filter.field_id);
    for (const key in filter) {
      const filterKey = key as keyof typeof filter;
      const param = filter[filterKey];
      if (key === 'label' || key === 'field_id' || param === undefined) {
        continue;
      }
      if (key in registryFilterMap) {
        filterTags.push({
          label: `${field?.text || ''} ${
            registryFilterMap[key as keyof typeof registryFilterMap]
          }`,
          id: filter.field_id,
          value:
            (field &&
              renderTagValue(param, key as keyof typeof registryFilterMap)) ||
            '',
          filterType: filterKey,
        });
      }
    }
  });
  return filterTags;
};

export const updateTitle = <T extends State>(
  state: T,
  title: string | symbol | null | undefined,
  id?: number,
  name?: string
): string => {
  try {
    switch (title) {
      case 'TaskList': {
        return 'Все задачи';
      }
      case 'DeepSearchTaskList': {
        return `Расширенный поиск: ${state.deepSearchTabs.join('; ')}`;
      }
      case 'FormList': {
        return 'Формы';
      }
      case 'Form': {
        return state.form?.name || state.appTitle;
      }
      case 'Task':
      case 'TaskIncoming': {
        const formName = state.task.form_name || '';
        const taskName = joinString(state.task.name ?? []);
        if (!formName && !taskName) {
          return state.appTitle;
        }
        return formName && taskName
          ? `${formName}: ${taskName}`
          : formName || taskName;
      }
      case 'TaskListIncoming': {
        return state.unreadTaskCounter > 0
          ? `${state.unreadTaskCounter} Входящие`
          : `Входящие`;
      }
      case 'FormConstructor': {
        return `Конструктор формы`;
      }
      case 'CatalogList': {
        return `Справочник`;
      }
      case 'Catalog': {
        return name || state.appTitle;
      }
      case 'Login': {
        return 'Войти';
      }
      case 'Company': {
        return 'Компания';
      }
      case 'Registry': {
        return name ? `Реестр ${name}` : state.appTitle;
      }
      case 'Profile': {
        return name || state.appTitle;
      }
      case 'Bot': {
        return name || state.appTitle;
      }
      case 'Department': {
        return name || state.appTitle;
      }
      default: {
        return 'Rulse';
      }
    }
  } catch (e) {
    return 'Rulse';
  }
};

export const getReadUsers = (
  ids: number[],
  currentUserId: number,
  lastMessageAuthorId: number
): User[] => {
  const users: User[] = [];
  for (let i = 0; i < ids.length; i++) {
    const user = getUserById(ids[i]);
    if (user && ids[i] !== currentUserId && ids[i] !== lastMessageAuthorId) {
      users.push(user);
    }
  }
  return users;
};

export const getSettings = async (): Promise<UserSettings> => {
  const settings = await getUserSettings();
  GlobalStore.dispatch('setUserSettings', settings);
  return settings;
};

export const removeSpanFromComment = (comment: string): string => {
  const withoutSpan = comment
    .replace(/<\/?span[^>]*>/g, '')
    .replace(/<\/?div[^>]*>/g, ' ');
  const withLineBreaks = withoutSpan.replace(/<br\s*\/?>/g, '\n');
  const withSpaces = withLineBreaks.replace(/&nbsp;/g, ' ');
  return withSpaces;
};

export const removeUnnecessaryStringAt = (string: string) => {
  return string
    .replaceAll(/&lt;\/?br&gt;/g, '')
    .replaceAll(/<\/?br>/g, '')
    .replaceAll(`&nbsp;`, '')
    .trim();
};

export const removeAnchorTags = (inputString: string): string => {
  const regex = /<a\b[^>]*>(.*?)<\/a>/gi;
  return inputString.replace(regex, '$1');
};

export const removeHtmlTagsAndAttributes = (inputString: string): string => {
  // Регулярное выражение для удаления тегов и их атрибутов
  const regex =
    /<[^>]*(\s+[^=\s]+(?:\s*=\s*(?:"[^"]*"|'[^']*'|[^'">\s]+))?)*\s*>/g;
  return inputString.replace(regex, '');
};

export const getParseFieldId = (
  message: string
): { id: number | null; rowId: number | null }[] => {
  const regex = /id (\d+), row id (\d+)/g;
  const matches = message.matchAll(regex);

  const result: { id: number | null; rowId: number | null }[] = [];

  for (const match of matches) {
    const [, idStr, rowIdStr] = match;

    const id = parseInt(idStr, 10);
    const rowId = parseInt(rowIdStr, 10);

    result.push({
      id: isNaN(id) ? null : id,
      rowId: isNaN(rowId) ? null : rowId,
    });
  }

  return result;
};

export const replaceNewlinesWithBr = (inputString: string): string => {
  return inputString.replace(/\n/g, '<br/>');
};

// TODO проверить функцию. На рудсе есть баг c удалением сообщений с одним и тем же контентом
export const removeDuplicates = <T extends { id: number }>(arr: T[]): T[] => {
  const uniqueIds: Set<number> = new Set();
  const uniqueArray: T[] = [];

  for (const obj of arr) {
    if (!uniqueIds.has(obj.id)) {
      uniqueIds.add(obj.id);
      uniqueArray.push(obj);
    }
  }

  return uniqueArray;
};

export const replaceHtmlEntities = (inputString: string): string => {
  // Заменяем &nbsp; на пробел и удаляем остальные HTML-сущности
  return inputString.replace(/&nbsp;/g, ' ').replace(/&[^;]+;/g, '');
};

export const createLatinHash = async (inputString: string): Promise<string> => {
  // Заменяем кириллические символы на их индексы в алфавите
  const cyrillicReplaced = inputString.replace(
    /[а-яА-Я]/g,
    (match) =>
      '%' +
      (
        match.charCodeAt(0) -
        (match >= 'а' ? 'а'.charCodeAt(0) : 'А'.charCodeAt(0))
      ).toString(16)
  );

  // Оставляем только цифры и латинские символы
  const alphanumericOnly = cyrillicReplaced.replace(/[^a-zA-Z0-9]/g, '');

  // Преобразуем строку в буфер
  const buffer = new TextEncoder().encode(alphanumericOnly);

  // Создаем хэш
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);

  // Преобразуем буфер хэша в строку
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');

  return hashHex;
};

export const findElementWithMinCreatedAt = <T extends { created_at: number }>(
  dataArray: T[]
): T | null => {
  if (dataArray.length === 0) {
    return null; // Возвращаем null, если массив пустой
  }

  let minCreatedAtElement = dataArray[0];

  for (const item of dataArray) {
    if (item.created_at < minCreatedAtElement.created_at) {
      minCreatedAtElement = item;
    }
  }

  return minCreatedAtElement;
};

export const createClickableLinks = (inputText: string): string => {
  let hasLink = false; // Флаг, указывающий, была ли найдена хотя бы одна ссылка

  // Функция для замены текста на соответствующий тег span с сохранением символов новой строки
  const replaceNonLink = (nonLink: string) => {
    // Заменяем символы новой строки на тег <br>, если предшествующий символ - \n
    const textWithLineBreaks = nonLink.replace(/\n/g, '\n<br>');
    // Заменяем текст без тега <span> обернутым в <span> с классом field-text
    return `<span class="field-text">${textWithLineBreaks}</span>`;
  };

  // Заменяем ссылки и текст на соответствующие теги
  let result = inputText.replace(
    /(\n)?(https?:\/\/\S+)|([^<>\s]+)/g,
    (match, lineBreak, link, nonLink) => {
      if (link !== undefined) {
        hasLink = true;
        // Если перед ссылкой стоит \n, добавляем <br> перед тегом <a>
        const lineBreakTag = lineBreak ? '<br>' : '';
        // Оборачиваем ссылку в тег <a>
        return `${lineBreakTag}<a href="${match}" class="clickable-link" target="_blank">${match}</a>`;
      } else {
        return replaceNonLink(nonLink);
      }
    }
  );

  // Если ссылка не была найдена, обернуть исходную строку в один тег <span>
  if (!hasLink) {
    result = `<span>${result}</span>`;
  }

  // Добавление <br> после </a>, если в конце ссылки есть \n
  result = result.replace(/<\/a>\n/g, '</a><br>');

  // Объединение соседних тегов <span> и <a> с сохранением пробелов и символов новой строки
  result = result.replace(
    /<\/span>(\s*<br>)?(\s*)<span[^>]*>|<\/span>(\s*<a[^>]*>)?(\s*)<\/a>(\s*)<span[^>]*>/g,
    (_, br1, space1, a, space2, br2, space3) => {
      return br1 || space1 || a || space2 || br2 || space3 || ' ';
    }
  );

  return result;
};

export const replaceHtmlSpaces = (input: string | undefined) => {
  if (input) return input.replace(/&nbsp;/g, ' ');
  return undefined;
};

export const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export const truncateSecondsFromTimestamp = (
  timestamp: number | null
): number | null => {
  if (timestamp === null) return null;
  const dateObject: Date = new Date(timestamp * 1000);
  dateObject.setSeconds(0);
  return Math.floor(dateObject.getTime() / 1000);
};
